import { AsyncButton } from '@components/AsyncButton'
import { ChipsInput } from '@components/ChipsInput/ChipsInput'
import { isValidEmail } from '@helpers/strings'
import {
	Box,
	Button,
	Divider,
	FormHelperText,
	Modal,
	Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface ModalProps {
	open: boolean
	onClose: () => void
	onActionClick: (data: readonly string[]) => void
	headerText: string
	errors: readonly string[]
}

const InviteModal = ({
	open,
	onClose,
	onActionClick,
	headerText,
	errors,
}: ModalProps) => {
	const { t } = useTranslation()
	const { enqueueSnackbar } = useSnackbar()
	const [email, setEmail] = useState('')

	const form = useForm<{ emails: readonly string[] }>({
		defaultValues: {
			emails: [],
		},
	})

	const handleEmailsChange = useCallback(
		(items: readonly string[]) => {
			form.setValue('emails', items)
		},
		[form]
	)

	const [isSending, setIsSending] = useState(false)

	const handleSend = useCallback(async () => {
		const { emails } = form.getValues()
		if (emails.length > 0) {
			if (await form.trigger(['emails'])) {
				await onActionClick(emails)
			}
		} else {
			if (!isValidEmail(email)) {
				enqueueSnackbar(t('formField.incorrectEmail'), {
					variant: 'error',
				})

				return
			}

			await onActionClick([email])
		}
	}, [email, enqueueSnackbar, form, onActionClick, t])

	const cancelSendInvitation = useCallback(() => {
		form.reset()
		onClose()
	}, [form, onClose])

	return (
		<Modal open={open} onClose={onClose}>
			<S.BackdropWithModalStyles>
				<Typography variant="h2">{headerText}</Typography>
				<Box pb={4} pt={1}>
					<Divider />
				</Box>

				<Typography
					variant="subtitle2"
					component="h5"
					color="textSecondary"
					style={{ marginBottom: '20px' }}
				>
					{t('inviteModal.subtitleLineOne')}
					<br />
					{t('inviteModal.subtitleLineTwo')}
				</Typography>

				<Box display="flex" flexDirection="column" width={350}>
					<FormProvider {...form}>
						<Box mb={4}>
							<ChipsInput
								emailValidation
								selectedChips={handleEmailsChange}
								variant="outlined"
								id="emails"
								name="emails"
								placeholder={t('formField.multipleEmailsPlaceHolder')}
								label={t('formField.emailAddressRequired')}
								invalidEmailLabel={t('formField.incorrectEmail')}
								onValueChange={setEmail}
							/>
							{errors.map((item, index) => (
								<FormHelperText error key={index}>
									{item}
								</FormHelperText>
							))}
						</Box>
					</FormProvider>
				</Box>

				<div className="add-members-modal-action-part">
					<Button
						className="add-members-modal-cancel-button"
						disabled={isSending}
						variant="outlined"
						color="primary"
						data-testid="cancel"
						onClick={cancelSendInvitation}
					>
						{t('button.cancel')}
					</Button>
					<AsyncButton
						variant="contained"
						color="primary"
						data-testid="submit"
						onClick={handleSend}
						onIsLoadingUpdated={setIsSending}
					>
						{t('button.sendInvitation')}
					</AsyncButton>
				</div>
			</S.BackdropWithModalStyles>
		</Modal>
	)
}

export { InviteModal }
